import { clsx } from 'clsx';
import { InputNumber } from 'antd';
import { InfoCircleFilled } from '@ant-design/icons';
import { useController, useWatch } from 'react-hook-form';
import { useEffect, useState } from 'react';

import { creditRiskHttp, TablePastCycleRow } from '@network';
import { LightTooltip } from '@components';
import cls from './table-past-cycle.module.css';

type Column = 'title' | 'value';
type Row = TablePastCycleRow;

interface Props {
  data: {rows: Row[]} | null,
  onChange: any;
  title: string;
  isFixed?: boolean;
}

export const TablePastCycle = ({ data, onChange, title, isFixed }: Props) => {
  const financialRatiosDscr = useWatch({ name: 'financialRatiosDscr' });
  const financialRatiosCashRatio = useWatch({ name: 'financialRatiosCashRatio' });
  const [defaultData, setDefaultData] = useState<{rows: Row[]}>({ rows: [] });

  useEffect(() => {
    (async () => {
      const dataDefault = await creditRiskHttp.getDefaultPastCycle();
      setDefaultData(dataDefault);
      if (!Array.isArray(data?.rows)) {
        setTimeout(() => void onChange(dataDefault), 500);
      }
    })();
  }, []);

  const onFieldChange = (val: number | null | string, key: Column, index: number) => {
    if (val === null) return;
    const nextRows = (data || defaultData)
      .rows.map((row, i) => i === index ? { ...row, [key]: val } : row);
    const nextData = { ...data, rows: nextRows };
    onChange(nextData);
  };

  const setField = (fields: Record<string, number | string>) => {
    const nextRows = (data || defaultData).rows.map(row => {
      if (fields[row.title] !== undefined) {
        return { ...row, value: fields[row.title] };
      }
      return row;
    });
    const nextData = { ...data, rows: nextRows };
    onChange(nextData);
  };

  useEffect(() => {
    setTimeout(() => {
      if (!Array.isArray(data?.rows)) {
        onChange(defaultData);
      }
    }, 500);
  }, [data]);

  useEffect(() => {
    setTimeout(() => {
      setField({
        'Cash Ratio': financialRatiosCashRatio,
        'DSCR': financialRatiosDscr,
      });
    }, 100);
  }, [financialRatiosCashRatio, financialRatiosDscr]);

  const rows = data?.rows || [] as Row[];

  return (
    <div className={cls.block}>
      <div className={cls.table}>
        <div className={cls.header}>
          <div className={cls.headerRow}>
            <div className={cls.headerCol}>{title}</div>
          </div>
        </div>
        <div className={cls.body}>
          {rows.map((row, index) => (
            <div key={index}>
              {typeof row.separator === 'string' && (
                <div className={cls.separator}>{row.separator}</div>
              )}
              <div className={clsx(cls.row, cls.rowColored, row.disabled && cls.rowDisabled)}>
                <div className={clsx(cls.col, cls.colTitle)}>
                  {row.title}
                </div>
                <div className={clsx(cls.col, !row.disabled && cls.colEditable)}>
                  <Input row={row}
                    field={'value'}
                    onChange={onFieldChange}
                    index={index}
                    isFixed={isFixed || row.disabled}
                    postfix={row.postfix}
                  />
                </div>
                {row.tooltip && (
                  <LightTooltip title={row.tooltip} extraWidth>
                    <div className={cls.tooltip}><InfoCircleFilled /></div>
                  </LightTooltip>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

interface InputProps {
  row: Row;
  field: Column;
  onChange?: any;
  index?: number;
  isFixed?: boolean;
  postfix?: string;
}

const Input = ({ row, field, onChange, index, isFixed, postfix }: InputProps) => {
  const value = row[field];
  const prefixer = row.format === 'money' ? '$ ' : '';
  const postfixer = postfix
    ? postfix
    : row.format === 'percent' ? ' %' : '';

  return (
    <InputNumber
      min={0}
      max={1000000000}
      onBeforeInputCapture={e => {
        const value = e.currentTarget.value;
        if (value && value.includes('.')) {
          const [, second] = value.split('.');
          if (second.length >= 2) {
            e.stopPropagation();
            e.preventDefault();
          }
        }
      }}
      formatter={(val) => `${prefixer}${val}${postfixer}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
      parser={(val) => {
        let nextVal: number = val ? +val.replace(/\$\s?|\s?\%|(,*)/g, '') : 0;
        if (row.title === 'Adjusting Variable') {
          if (nextVal < 0) nextVal = 0;
          if (nextVal > 100) nextVal = 100;
        }

        return nextVal;
      }}
      value={value}
      bordered={false}
      onChange={val => onChange(val, field, index)}
      className={cls.input}
      disabled={isFixed}
      controls={false}
    />
  );
};
